/**
 * app/index.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */
import 'babel-polyfill'
import {createStore, applyMiddleware, combineReducers} from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly'
import { routerReducer, routerMiddleware } from 'react-router-redux'
import { browserHistory } from 'react-router'
import createSagaMiddleware from 'redux-saga'
import createLogger from 'redux-logger'
import reducer from 'reducers'
import rootSaga from 'sagas'
import authSaga from 'sagas/auth'
import outletSaga from 'sagas/outlet'
import reportsSalesSaga from 'sagas/reports/sales'
import reportsShiftsSaga from 'sagas/reports/shifts'
import reportsInvoicesSaga from 'sagas/reports/invoices'
import calendarSaga from 'sagas/calendar'
import dashboardSaga from 'sagas/dashboard/index'
import reportsTransactionsSaga from 'sagas/reports/transactions'
import getExportedFileSaga from 'sagas/get-exported-file'
import settingAccountSaga from 'sagas/setting/account'
import settingPublicProfileSaga from 'sagas/setting/publicProfile'
import uploadImageSaga from 'sagas/uploadImage/index'
import importCustomers from 'sagas/uploaders/importCustomer'
import settingCheckoutSaga from 'sagas/setting/checkout'
import settingReceiptSaga from 'sagas/setting/receipt'
import settingBillingSaga from 'sagas/setting/billing'
import confirmActionSaga from 'sagas/confirm-action'
import notificationSaga from 'sagas/notifications'
import feedbackReplyFeedbackSaga from 'sagas/feedbackReplyForm'
import verifyEmailSaga from 'sagas/verifyEmail'
import billingRedirectSaga from 'sagas/billingRedirect'
import billingNotificationSaga from 'sagas/billingNotification'
import * as customerSagas from 'sagas/customers'
import * as settingSagas from 'sagas/setting'
import * as employeeSagas from 'sagas/employees'
import * as outletPageSagas from 'sagas/outletPages'
import * as librarySagas from 'sagas/library'
import * as exampleFormSagas from 'sagas/example/form'
import * as inventorySagas from 'sagas/inventory'
import * as ingredientSagas from 'sagas/ingredients'
import * as forgotPasswordSagas from 'sagas/forgotPassword'
import confirmEmployeeSaga from 'sagas/confirmEmployee'
import feedbackFormSaga from 'sagas/feedbackForm'
import subscriptionExpiredSaga from 'sagas/subscriptionExpired'
import verifyEmailAlertRecipientSaga from 'sagas/verifyEmailAlertRecipient'

import {reducer as dashboardIndexReducer} from 'reducers/dashboard/index'
import {reducer as formReducer} from 'redux-form'
import {reducer as notificationReducer} from 'reducers/notifications'
import {reducer as authReducer} from 'reducers/auth'
import {reducer as outletReducer} from 'reducers/outlet'
import {reducer as reportTransactionReducer} from 'reducers/reports/transactions'
import {reducer as settingReceiptReducer} from 'reducers/setting/receipt'
import {reducer as settingBillingReducer} from 'reducers/setting/billing'
import {reducer as billingNotificationReducer} from 'reducers/billingRedirect/billingRedirect'
import {
  reducer as salesSummaryReducer,
  reducer as grossProfitReducer,
  reducer as paymentMethodsReducer,
  reducer as itemSalesReducer,
  reducer as categorySalesReducer,
  reducer as modifierSalesReducer,
  reducer as discountsReducer,
  reducer as taxesReducer,
  reducer as gratuityReducer,
  reducer as collectedByReducer,
  reducer as servedByReducer,
  reducer as loadingSpinnerReportReducer
} from 'reducers/reports/sales'
import actionBarReducer from 'reducers/actionBar'
import {reducer as reportsShiftsReducer} from 'reducers/reports/shifts'
import {reducer as reportsInvoicesReducer} from 'reducers/reports/invoices'
import {reducer as calendarReducer} from 'reducers/calendar'
import reduxSentryMiddleware from 'middleware/redux.sentry.middleware'
import {reducer as settingAccountReducer} from 'reducers/setting/account'
import {reducer as uploadImageReducer} from 'reducers/uploadImage/index'
import {reducer as uploadersReducer} from 'reducers/uploaders/index'
import {reducer as buttonReducer} from 'reducers/button/index'
import {reducer as feedbackReplyReducers} from 'reducers/feedbackReplyForm/index'
import {reducer as settingCheckoutReducer} from 'reducers/setting/checkout'
import {reducer as confirmActionReducer} from 'reducers/confirm-action'
import * as customerReducers from 'reducers/customers'
import * as settingReducers from 'reducers/setting'
import * as employeeReducers from 'reducers/employees'
import * as outletPageReducers from 'reducers/outletPages'
import * as libraryReducers from 'reducers/library'
import * as inventoryReducers from 'reducers/inventory'
import * as ingredientReducers from 'reducers/ingredients'
import {reducer as verifyEmailReducer} from 'reducers/verifyEmail/index'
import {reducer as confirmEmployeeReducer} from 'reducers/confirmEmployee/index'
import {reducer as loadingSpinnerReducer} from 'reducers/loadingSpinner/index'
import {reducer as feedbackFormReducer} from 'reducers/feedbackForm/index'
import {reducer as subscriptionExpiredReducer} from 'reducers/subscriptionExpired/index'
import {reducer as verifyEmailAlertRecipientReducer} from 'reducers/verifyEmailAlertRecipient/index'

const SENTRY_DSN = process.env.SENTRY_DSN
if (process.env.MIXPANEL_TOKEN) {
  window.mixpanel.init(process.env.MIXPANEL_TOKEN)
}

const logger = createLogger({
  // Ignore `CHANGE_FORM` actions in the logger, since they fire after every keystroke
  predicate: (getState, action) => {
    return action.type !== 'CHANGE_FORM' &&
        action.type !== '@@redux-form/REGISTER_FIELD' &&
        action.type !== '@@redux-form/UNREGISTER_FIELD'
  }
})

const sagaMiddleware = createSagaMiddleware()

// Creates the Redux store using our reducer and the logger and saga middlewares
const middlewares = [ sagaMiddleware ]

if (SENTRY_DSN) {
  middlewares.unshift(reduxSentryMiddleware(SENTRY_DSN, {
    environment: process.env.NODE_ENV,
    autoBreadcrumbs: {
      'xhr': true,      // XMLHttpRequest
      'console': false,  // console logging
      'dom': true,       // DOM interactions, i.e. clicks/typing
      'location': true  // url changes, including pushState/popState
    },
    release: `${__MOKA_VERSION__}` // from webpack.common.js
  }))
}

// https://github.com/reactjs/react-router-redux
middlewares.unshift(routerMiddleware(browserHistory))

if (process.env.NODE_ENV === 'development') {
  window.Perf = require('react-addons-perf')
  middlewares.unshift(logger)
}

const reducers = {
  routing: routerReducer, // reducer from react-router-redux. Please don't change the key name
  moka: reducer, // moka default reducer. Please don't change the key name,
  actionBar: actionBarReducer,
  form: formReducer, // redux form reducer. Please don't change the key name
  calendar: calendarReducer, // for dropdown calendar
  notification: notificationReducer, // our custom reducer for notification.
  auth: authReducer, // auth specific reducer
  outlet: outletReducer, // outlet for related user
  library: combineReducers({
    category: libraryReducers.libraryCategoryReducer,
    discount: libraryReducers.libraryDiscountReducer,
    gratuity: libraryReducers.libraryGratuityReducer,
    item: libraryReducers.libraryItemReducer,
    modifier: libraryReducers.libraryModifierReducer,
    tax: libraryReducers.libraryTaxReducer
  }),
  reports: combineReducers({
    sales: salesSummaryReducer,
    grossProfit: grossProfitReducer,
    paymentMethods: paymentMethodsReducer,
    itemSales: itemSalesReducer,
    categorySales: categorySalesReducer,
    modifierSales: modifierSalesReducer,
    discounts: discountsReducer,
    taxes: taxesReducer,
    gratuities: gratuityReducer,
    staffs: collectedByReducer,
    servers: servedByReducer,
    shifts: reportsShiftsReducer,
    transactions: reportTransactionReducer,
    invoices: reportsInvoicesReducer,
    showLoadingSpinnerReport: loadingSpinnerReportReducer
  }),
  ingredients: combineReducers({
    library: ingredientReducers.libraryReducer,
    category: ingredientReducers.categoryReducer,
    recipes: ingredientReducers.recipesReducer,
    ingredientSubscription: ingredientReducers.ingredientSubscriptionReducer
  }),
  inventory: combineReducers({
    summary: inventoryReducers.summaryReducer,
    supplier: inventoryReducers.supplierReducer,
    purchaseOrder: inventoryReducers.purchaseOrderReducer,
    transfer: inventoryReducers.transferReducer,
    adjustment: inventoryReducers.adjustmentReducer,
    ingredientSubscription: inventoryReducers.ingredientSubscriptionReducer
  }),
  dashboard: combineReducers({index: dashboardIndexReducer}),
  setting: combineReducers({
    account: settingAccountReducer,
    checkout: settingCheckoutReducer,
    receipt: settingReceiptReducer,
    emailNotification: settingReducers.emailNotificationReducer,
    billing: settingBillingReducer
  }),
  uploadImage: combineReducers({uploadImage: uploadImageReducer}),
  confirmAction: confirmActionReducer,
  customers: combineReducers({
    feedback: customerReducers.customerFeedbackReducer,
    list: customerReducers.customerListReducer,
    loyalty: customerReducers.customerLoyaltyReducer
  }),
  employee: combineReducers({
    permission: employeeReducers.employeePermissionReducer,
    list: employeeReducers.employeeListReducer
  }),
  outletPages: combineReducers({
    list: outletPageReducers.outletPagesListReducer
  }),
  uploaders: combineReducers({
    import: uploadersReducer
  }),
  buttons: buttonReducer,
  feedback: combineReducers({
    replyForm: feedbackReplyReducers
  }),
  verifyEmail: verifyEmailReducer,
  confirmEmployee: confirmEmployeeReducer,
  loadingSpinner: loadingSpinnerReducer,
  feedbackForm: feedbackFormReducer,
  subscriptionExpired: subscriptionExpiredReducer,
  billingNotification: billingNotificationReducer,
  verifyEmailAlertRecipient: verifyEmailAlertRecipientReducer
}

const store = createStore(combineReducers(reducers), composeWithDevTools(
  applyMiddleware(...middlewares)
))

// We run the root saga automatically
sagaMiddleware.run(rootSaga)
sagaMiddleware.run(authSaga)
sagaMiddleware.run(forgotPasswordSagas.forgotPasswordSaga)
sagaMiddleware.run(outletSaga)
sagaMiddleware.run(reportsSalesSaga)
sagaMiddleware.run(reportsShiftsSaga)
sagaMiddleware.run(reportsInvoicesSaga)
sagaMiddleware.run(calendarSaga)
sagaMiddleware.run(dashboardSaga)
sagaMiddleware.run(reportsTransactionsSaga)
sagaMiddleware.run(getExportedFileSaga)
sagaMiddleware.run(settingAccountSaga)
sagaMiddleware.run(settingPublicProfileSaga)
sagaMiddleware.run(uploadImageSaga)
sagaMiddleware.run(importCustomers)
sagaMiddleware.run(settingCheckoutSaga)
sagaMiddleware.run(settingReceiptSaga)
sagaMiddleware.run(confirmActionSaga)
sagaMiddleware.run(feedbackReplyFeedbackSaga)
sagaMiddleware.run(settingSagas.emailNotificationSaga)
sagaMiddleware.run(customerSagas.customerFeedbackSaga)
sagaMiddleware.run(customerSagas.customerListSaga)
sagaMiddleware.run(customerSagas.loyaltySaga)
sagaMiddleware.run(employeeSagas.employeePermissionSaga)
sagaMiddleware.run(employeeSagas.employeeListSaga)
sagaMiddleware.run(outletPageSagas.outletPagesListSaga)
sagaMiddleware.run(librarySagas.libraryCategorySaga)
sagaMiddleware.run(librarySagas.libraryDiscountSaga)
sagaMiddleware.run(librarySagas.libraryItemSaga)
sagaMiddleware.run(librarySagas.libraryGratuitySaga)
sagaMiddleware.run(librarySagas.libraryModifierSaga)
sagaMiddleware.run(librarySagas.libraryTaxSaga)
sagaMiddleware.run(exampleFormSagas.dropdownCalendarFormSaga)
sagaMiddleware.run(notificationSaga)
sagaMiddleware.run(ingredientSagas.librarySaga)
sagaMiddleware.run(ingredientSagas.categorySaga)
sagaMiddleware.run(ingredientSagas.ingredientSubscriptionSaga)
sagaMiddleware.run(ingredientSagas.recipesSaga)
sagaMiddleware.run(inventorySagas.summarySaga)
sagaMiddleware.run(inventorySagas.supplierSaga)
sagaMiddleware.run(inventorySagas.purchaseOrderSaga)
sagaMiddleware.run(inventorySagas.transferSaga)
sagaMiddleware.run(inventorySagas.adjustmentSaga)
sagaMiddleware.run(inventorySagas.ingredientSubscriptionSaga)
sagaMiddleware.run(verifyEmailSaga)
sagaMiddleware.run(confirmEmployeeSaga)
sagaMiddleware.run(feedbackFormSaga)
sagaMiddleware.run(settingBillingSaga)
sagaMiddleware.run(verifyEmailSaga)
sagaMiddleware.run(subscriptionExpiredSaga)
sagaMiddleware.run(billingRedirectSaga)
sagaMiddleware.run(billingNotificationSaga)
sagaMiddleware.run(verifyEmailAlertRecipientSaga)

const MokaBackofficeRoutes = require('./routes')(store)
ReactDOM.render(<MokaBackofficeRoutes />, document.getElementById('app'))



// WEBPACK FOOTER //
// ./app/index.js